import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { GameResult } from "@topzle/types";
import { z } from "zod";

const cards = [
  { target: "ocean", forbidden: ["water", "sea", "waves", "blue"] },
  { target: "camera", forbidden: ["photo", "picture", "lens", "video"] },
  { target: "library", forbidden: ["books", "read", "quiet", "shelf"] },
];
export const tabooStateSchema = z.object({
  players: z.array(z.string()),
  activeIndex: z.number().int(),
  cardIndex: z.number().int(),
  scores: z.record(z.string(), z.number()),
  phase: z.enum(["ready", "playing", "finished"]),
  deadline: z.number().nullable(),
});
export const tabooEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start") }),
  z.object({ type: z.literal("correct") }),
  z.object({ type: z.literal("skip") }),
  z.object({ type: z.literal("violation") }),
  z.object({ type: z.literal("finish") }),
]);
export const tabooSettingsSchema = z.object({
  roundSeconds: z.number().int().min(30).max(180).default(60),
  rounds: z.number().int().min(1).max(30).default(8),
});
type State = z.infer<typeof tabooStateSchema>;
type Event = z.infer<typeof tabooEventSchema>;
type Settings = z.infer<typeof tabooSettingsSchema>;
export const tabooGame: TopzleGameDefinition<
  State,
  Event,
  Settings,
  { card: (typeof cards)[number] | null }
> = {
  id: "taboo",
  version: "1.0.0",
  name: "AI Taboo",
  shortDescription: "Describe the target without the forbidden words.",
  longDescription:
    "Private cards, deterministic checks, stemming hooks, voice assistance, and manual referee control.",
  category: "party",
  icon: "message-off",
  cover: "taboo",
  minPlayers: 4,
  maxPlayers: 12,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: true,
  supportsAiPlayers: false,
  supportsLocalAi: true,
  supportsCloudAi: true,
  supportsTournaments: false,
  supportsPersistence: true,
  requiredCapabilities: ["audio"],
  supportedLayouts: ["active_speaker", "grid", "avatar_only"],
  preferredLayout: "active_speaker",
  stateSchema: tabooStateSchema,
  eventSchema: tabooEventSchema,
  settingsSchema: tabooSettingsSchema,
  createInitialState(context) {
    return {
      players: context.playerIds,
      activeIndex: 0,
      cardIndex: 0,
      scores: Object.fromEntries(context.playerIds.map((id) => [id, 0])),
      phase: "ready",
      deadline: null,
    };
  },
  validateEvent(state, event, context) {
    if (event.type === "finish") return { valid: true };
    return {
      valid: context.actorId === state.players[state.activeIndex],
      code: "not_referee",
      message: "Only the active player or referee can score",
    };
  },
  reduceEvent(state, event, context) {
    if (event.type === "finish") return { ...state, phase: "finished", deadline: null };
    if (event.type === "start")
      return { ...state, phase: "playing", deadline: context.now + 60_000 };
    const scores = { ...state.scores };
    const active = state.players[state.activeIndex];
    if (event.type === "correct" && active) scores[active] = (scores[active] ?? 0) + 1;
    if (event.type === "violation" && active)
      scores[active] = Math.max(0, (scores[active] ?? 0) - 1);
    return {
      ...state,
      scores,
      cardIndex: (state.cardIndex + 1) % cards.length,
      activeIndex: (state.activeIndex + 1) % state.players.length,
      phase: "ready",
      deadline: null,
    };
  },
  getPublicState: (state) => ({ ...state, card: undefined }),
  getPrivatePlayerState: (state, playerId) => ({
    card: playerId === state.players[state.activeIndex] ? (cards[state.cardIndex] ?? null) : null,
  }),
  getSpectatorState: (state) => ({ ...state, card: undefined }),
  getAvailableActions: (state, playerId) =>
    playerId === state.players[state.activeIndex]
      ? [
          { type: state.phase === "ready" ? "start" : "correct" },
          { type: "skip" },
          { type: "violation" },
        ]
      : [],
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    const high = Math.max(...Object.values(state.scores));
    return {
      status: "completed",
      winnerIds: Object.entries(state.scores)
        .filter(([, v]) => v === high)
        .map(([id]) => id),
      scores: state.scores,
      reason: "rounds_complete",
    };
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 12 ||
      state.players.includes(arrival.playerId)
    )
      return null;
    return {
      state: {
        ...state,
        players: [...state.players, arrival.playerId],
        scores: { ...state.scores, [arrival.playerId]: 0 },
      },
    };
  },
  handlePlayerDeparture(state, departure) {
    const removedIndex = state.players.indexOf(departure.playerId);
    if (removedIndex < 0 || state.players.length <= 1) return null;
    const players = state.players.filter((id) => id !== departure.playerId);
    const removedActivePlayer = removedIndex === state.activeIndex;
    const scores = { ...state.scores };
    delete scores[departure.playerId];
    return {
      state: {
        ...state,
        players,
        scores,
        activeIndex: removedActivePlayer
          ? removedIndex % players.length
          : removedIndex < state.activeIndex
            ? state.activeIndex - 1
            : state.activeIndex,
        phase: removedActivePlayer && state.phase === "playing" ? "ready" : state.phase,
        deadline: removedActivePlayer ? null : state.deadline,
      },
    };
  },
};
